/**
 * Menu → Deals (F-DEAL-N1..N4): combos, automatic discounts and upsells.
 * The sub-tab is remembered for the session so editing an item from the
 * Upsells pane comes back to Upsells.
 */
import { useState } from 'react'
import { Package, Percent, Sparkles } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import type { Product } from '@/types'
import { Segmented } from './DealsControls'
import { CombosPane } from './CombosPane'
import { AutoDiscountsPane } from './AutoDiscountsPane'
import { UpsellsPane } from './UpsellsPane'

type Pane = 'combos' | 'auto' | 'upsells'
const PANES: Pane[] = ['combos', 'auto', 'upsells']
const STORAGE_KEY = 'menu.deals.pane'

function initialPane(): Pane {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY)
    if (v && (PANES as string[]).includes(v)) return v as Pane
  } catch {
    /* storage unavailable */
  }
  return 'combos'
}

export function AdminDealsTab({ onEditProduct }: { onEditProduct?: (p: Product) => void }) {
  const [pane, setPaneState] = useState<Pane>(initialPane)
  const setPane = (p: Pane) => {
    setPaneState(p)
    try {
      sessionStorage.setItem(STORAGE_KEY, p)
    } catch {
      /* storage unavailable */
    }
  }

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <Segmented<Pane>
          ariaLabel="Deals"
          value={pane}
          onChange={setPane}
          items={[
            { id: 'combos', label: 'Combos', icon: <Package className="h-4 w-4" /> },
            { id: 'auto', label: 'Automatic discounts', icon: <Percent className="h-4 w-4" /> },
            { id: 'upsells', label: 'Upsells', icon: <Sparkles className="h-4 w-4" /> },
          ]}
        />
        {pane === 'combos' && <CombosPane />}
        {pane === 'auto' && <AutoDiscountsPane />}
        {pane === 'upsells' && <UpsellsPane onEditProduct={onEditProduct} />}
      </CardContent>
    </Card>
  )
}
